import React, {Suspense, useState} from 'react';
import {useNavigate} from 'react-router';
import DOMPurify from 'dompurify';
import {diApi} from '@di-shared/api/di';
import {CreatePublicServiceConsentRequest, GetRenewalEligibleResponse} from '@di-shared/api/di/__generated';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import HeaderCloseButton from '@di-shared/features/common/HeaderCloseButton';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {useEnvironmentStore} from '@di-shared/features/environment/environmentStore';
import {ROUTE_PATHS} from '@di-shared/routes';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {useSuspenseQuery} from '@tanstack/react-query';

export interface PublicServiceConsentsDetailProps {
  vin: string;
  category: CreatePublicServiceConsentRequest.category;
  nextUrl: string;
}

function PublicServiceConsentsDetail(props: PublicServiceConsentsDetailProps) {
  return (
    <Suspense>
      <Inner {...props} />
    </Suspense>
  );
}

function Inner({vin, category, nextUrl}: PublicServiceConsentsDetailProps) {
  const {t} = useInferableTranslation();
  const {resolveError} = useApiError();
  const puid = useEnvironmentStore((state) => state.puid);
  const navigate = useNavigate();
  const {backOrClose} = useCcWebviewUtilsHelper();
  const {openAlert} = useCcWebviewUtilsHelper();

  const {data: termsData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.publicService.getPublicServiceTerms), puid, category],
    queryFn: () => diApi.publicService.getPublicServiceTerms({puid, termsCategory: category}),
  });
  const [selectedTerms, setSelectedTerms] = useState<Array<{termsId: number; status: boolean}>>([]);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [selectedTermId, setSelectedTermId] = useState<number>(0);
  const currentTerms = termsData.data.terms.find((termItem) => termItem.termsId === selectedTermId);

  const [agreeConsents] = usePendingHandler(
    async () => {
      // 운전면허갱신 검증
      if (category === CreatePublicServiceConsentRequest.category.LICENSE_RENEWAL) {
        const isValid = await handleLicenseRenewalValidation();
        if (!isValid) {
          return; // 서비스 이용 동의 불가
        }
      }

      // 벌점감경교육 검증
      if (category === CreatePublicServiceConsentRequest.category.DEMERIT_EDUCATION) {
        const isValid = await handleDemeritEducationValidation();
        if (!isValid) {
          return; // 서비스 이용 동의 불가
        }
      }

      await diApi.publicService.consentTerms({
        requestBody: {
          puid,
          category,
          consentTermsList: selectedTerms,
        },
      });

      await openAlert({
        title: t('T20355'),
        message: '',
      });

      navigate(nextUrl, {replace: true});
    },
    async (error) => {
      await resolveError(error);
    }
  );

  async function handleDemeritEducationValidation() {
    const eligibilityData = await diApi.demeritEducationController.getEligibility({});

    switch (eligibilityData.data.resultCode) {
      case 'C95':
      case 'C96':
      case 'C97':
      case 'C98':
      case 'C99':
        // 시스템 연계 오류
        await ccWebviewUtils.openBottomSheetWebViewUrl(
          ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_DEMERIT_EDUCATION_FAIL_SYSYEM_ERROR.getFullPath()
        );
        return false;
      case 'S1R':
      case 'S2R':
      case 'S3R':
        // 고객 정보 오류
        await ccWebviewUtils.openBottomSheetWebViewUrl(
          ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_DEMERIT_EDUCATION_FAIL_CUSTOMER_INFO.getFullPath()
        );
        return false;
      default:
        // 정상적인 케이스는 계속 진행
        return true;
    }
  }

  async function handleLicenseRenewalValidation() {
    const licenseRenewalData = await diApi.licenseRenewalController.getLicenseRenewal({});

    switch (licenseRenewalData.data.renewalType) {
      case GetRenewalEligibleResponse.renewalType.FAIL_CUSTOMER_INFO: // 고객 정보 오류
        await ccWebviewUtils.openBottomSheetWebViewUrl(
          ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_LICENSE_RENEWAL_FAIL_CUSTOMER_INFO.getFullPath()
        );
        return false;
      case GetRenewalEligibleResponse.renewalType.FAIL_SYSTEM_ERROR: // 시스템 정보 오류
        await ccWebviewUtils.openBottomSheetWebViewUrl(
          ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_LICENSE_RENEWAL_FAIL_SYSTEM_ERROR.getFullPath()
        );
        return false;
      case GetRenewalEligibleResponse.renewalType.FAIL_POLICE_CASE: // 경찰청 조회 결과 특수
        await ccWebviewUtils.openBottomSheetWebViewUrl(
          ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_LICENSE_RENEWAL_FAIL_POLICE_CASE.getFullPath()
        );
        return false;
      case GetRenewalEligibleResponse.renewalType.FAIL_OVER_75YEARS: // 75세 이상 고령자
        await ccWebviewUtils.openBottomSheetWebViewUrl(
          ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_LICENSE_RENEWAL_FAIL_OVER_75YEARS.getFullPath()
        );
        return false;
      default:
        // 정상적인 케이스는 계속 진행
        return true;
    }
  }

  function updateTerms(termsId: number) {
    const isAgree = selectedTerms.some((termItem) => termItem.termsId === termsId);
    if (isAgree) {
      setSelectedTerms((prev) => prev.filter((termItem) => termItem.termsId !== termsId));
      return;
    }
    openAndSelectTerms(termsId);
  }

  function openAndSelectTerms(termsId: number) {
    setSelectedTermId(termsId);
    setIsTermsOpen(true);
    setSelectedTerms((prev) => {
      if (prev.some((termItem) => termItem.termsId === termsId)) {
        return prev;
      }
      return [...prev, {termsId, status: true}];
    });
  }

  return isTermsOpen ? (
    <>
      <Header
        left={
          <HeaderBackButton
            onClick={() => {
              setIsTermsOpen(false);
            }}
          />
        }
      >
        <h1 className="title_xsmall_b nav_center_title">{t('T20405')}</h1>
      </Header>
      <main
        id="content"
        className="content"
        dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(currentTerms?.content || '')}}
      ></main>
    </>
  ) : (
    <>
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">{t('T30195')}</h1>
      </Header>
      <main
        id="content"
        className="content"
      >
        <section className="section section_car_service_consent">
          <div className="section_heading">
            <h2 className="section_heading_title">{termsData.data.categoryTitle}</h2>
            <p className="section_heading_desc">{termsData.data.categoryDescription}</p>
          </div>

          <div className="consent_agree">
            {termsData.data.terms.map((termItem) => (
              <React.Fragment key={termItem.termsId}>
                <div className="consent_agree_item">
                  <div className="input_box">
                    <input
                      type="checkbox"
                      id={`checkbox_${termItem.termsId}`}
                      className="input"
                      checked={selectedTerms.some((item) => item.termsId === termItem.termsId)}
                      onChange={() => updateTerms(termItem.termsId)}
                    />
                    <label
                      htmlFor={`checkbox_${termItem.termsId}`}
                      className="label"
                    >
                      {termItem.title}
                    </label>
                  </div>

                  <button
                    type="button"
                    className="btn btn_ic"
                    onClick={() => openAndSelectTerms(termItem.termsId)}
                  >
                    <span className="ic ic_arrow_forward_16px">
                      <span className="blind">{t('T10063')}</span>
                    </span>
                  </button>
                </div>
                <div className="divider"></div>
              </React.Fragment>
            ))}
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_outline btn_full"
            onClick={() => backOrClose()}
          >
            {t('T20182')}
          </button>

          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            disabled={termsData.data.terms.length !== selectedTerms.length}
            onClick={() => agreeConsents()}
          >
            {t('T10218')}
          </button>
        </div>
      </div>
    </>
  );
}

export default PublicServiceConsentsDetail;
